import React, { useState } from 'react';
import { Pagination, PaginationItem, PaginationLink, Input } from 'reactstrap';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import '../../ProfileQuestions/ProfileQuestions.css';

const APIAnalyticsTable = ({ apis }) => {
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: 'calls', direction: 'desc' });
    const [searchTerm, setSearchTerm] = useState('');

    const filteredApis = React.useMemo(() => {
        if (!searchTerm) return apis;
        try {
            const regex = new RegExp(searchTerm, 'i');
            return apis.filter(api => regex.test(api.path || '') || regex.test(api.feature || ''));
        } catch (e) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            return apis.filter(api => 
                (api.path && api.path.toLowerCase().includes(lowerSearchTerm)) || 
                (api.feature && api.feature.toLowerCase().includes(lowerSearchTerm))
            );
        }
    }, [apis, searchTerm]);

    const sortedApis = React.useMemo(() => {
        let sortableApis = [...filteredApis];
        if (sortConfig !== null) {
            sortableApis.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableApis;
    }, [filteredApis, sortConfig]);

    const startIndex = (page - 1) * itemsPerPage;
    const paginatedApis = sortedApis.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredApis.length / itemsPerPage);
    const totalRecords = filteredApis.length;

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const renderHeader = (label, key) => (
        <th onClick={() => requestSort(key)} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span>{label}</span>
                <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', fontSize: '11px', gap: '4px' }}>
                    <FaChevronUp color={sortConfig?.key === key && sortConfig.direction === 'asc' ? '#000' : '#cbd5e1'} />
                    <FaChevronDown color={sortConfig?.key === key && sortConfig.direction === 'desc' ? '#000' : '#cbd5e1'} />
                </span>
            </div>
        </th>
    );

    return (
        <div className="analytics-table-container mt-4">
            <div className="d-flex align-items-center mb-4" style={{ gap: '15px' }}>
                <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px', fontWeight: 600 }}>API Details</h3>
                <Input
                    type="text"
                    placeholder="Search API & Feature"
                    className="pq-filter-input"
                    style={{ width: '200px' }}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
            </div>

            <div className="pq-table-wrapper">
                <div className="pq-table-scroll">
                    <table className="pq-table">
                        <thead>
                            <tr>
                                {renderHeader('API Endpoint', 'path')}
                                {renderHeader('Feature', 'feature')}
                                {renderHeader('Method', 'method')}
                                {renderHeader('Calls', 'calls')}
                                {renderHeader('Success', 'successCount')}
                                {renderHeader('Failed', 'failureCount')}
                                {renderHeader('2xx', 'status2xx')}
                                {renderHeader('3xx', 'status3xx')}
                                {renderHeader('4xx', 'status4xx')}
                                {renderHeader('5xx', 'status5xx')}
                                {renderHeader('Success %', 'successRate')}
                                {renderHeader('Error %', 'errorRate')}
                                {renderHeader('Avg Resp', 'avgResponseTime')}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedApis.length > 0 ? paginatedApis.map((api, index) => (
                                <tr key={index}>
                                    <td>{api.path}</td>
                                    <td>{api.feature}</td>
                                    <td><span className={`status-badge`}>{api.method}</span></td>
                                    <td>{api.calls}</td>
                                    <td>{api.successCount}</td>
                                    <td>{api.failureCount}</td>
                                    <td><span className="status-badge status-2xx">{api.status2xx}</span></td>
                                    <td><span className="status-badge status-3xx">{api.status3xx}</span></td>
                                    <td><span className="status-badge status-4xx">{api.status4xx}</span></td>
                                    <td><span className="status-badge status-5xx">{api.status5xx}</span></td>
                                    <td><span style={{ color: api.successRate < 90 ? '#e67e22' : '#2ecc71' }}>{api.successRate || 0}%</span></td>
                                    <td><span style={{ color: api.errorRate > 10 ? '#e74c3c' : 'inherit' }}>{api.errorRate || 0}%</span></td>
                                    <td>{api.avgResponseTime}ms</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="13" style={{ textAlign: 'center', padding: '30px' }}>No APIs match the criteria.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {totalRecords > 0 && (
                    <div className="pq-pagination-container d-flex justify-content-between align-items-center p-3 border-top">
                        <div className="d-flex align-items-center">
                            <span className="pq-pagination-text me-2">
                                Showing {startIndex + 1} – {Math.min(startIndex + itemsPerPage, totalRecords)} of {totalRecords} entries. Per page: 
                            </span>
                            <Input 
                                type="select" 
                                value={itemsPerPage} 
                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }} 
                                className="pq-filter-input" 
                                style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important', margin: 0 }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </Input>
                        </div>

                        <Pagination size="sm" className="mb-0">
                            <PaginationItem className="pq-page-item" disabled={page === 1}>
                                <PaginationLink className="pq-page-link" previous onClick={() => setPage(page - 1)} />
                            </PaginationItem>

                            {[...Array(totalPages)].map((_, i) => {
                                if (i === 0 || i === totalPages - 1 || (i >= page - 2 && i <= page)) {
                                    return (
                                        <PaginationItem className="pq-page-item" active={i + 1 === page} key={i}>
                                            <PaginationLink className="pq-page-link" onClick={() => setPage(i + 1)}>
                                                {i + 1}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                if (i === 1 && page > 3) return <PaginationItem className="pq-page-item" disabled key="start-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                                if (i === totalPages - 2 && page < totalPages - 2) return <PaginationItem className="pq-page-item" disabled key="end-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                                return null;
                            })}

                            <PaginationItem className="pq-page-item" disabled={page === totalPages || totalPages === 0}>
                                <PaginationLink className="pq-page-link" next onClick={() => setPage(page + 1)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </div>
    );
};

export default APIAnalyticsTable;
